import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type MatchStoryCardContent,
  type MatchResultGoal,
  type MatchResultTeam,
} from "./types";

interface MatchStoryCardProps {
  format: CardFormat;
  content: MatchStoryCardContent;
  /** 0 = Cover, 1 = Result, 2 = Story. Defaults to 0. */
  pageIndex?: number;
}

export const MATCH_STORY_PAGE_COUNT = 3;

const MatchStoryCard = forwardRef<HTMLDivElement, MatchStoryCardProps>(
  function MatchStoryCard({ format, content, pageIndex = 0 }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const themeId = content.theme ?? "signature";
    const theme = resolveCardTheme(themeId, { monoColor: content.monoColor });
    const isEditorial = themeId === "editorial";

    const safePage = Math.max(0, Math.min(pageIndex, MATCH_STORY_PAGE_COUNT - 1));

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("MATCH_STORY")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {safePage === 0 && (
            <CoverPage
              content={content}
              isStory={isStory}
              isEditorial={isEditorial}
              theme={theme}
              style={style}
              format={format}
            />
          )}
          {safePage === 1 && (
            <ResultPage
              content={content}
              isStory={isStory}
              theme={theme}
              style={style}
              format={format}
            />
          )}
          {safePage === 2 && (
            <StoryPage
              content={content}
              isStory={isStory}
              isEditorial={isEditorial}
              theme={theme}
              style={style}
              format={format}
            />
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default MatchStoryCard;

// ─── Cover page ──────────────────────────────────────────────────────────────

function CoverPage({
  content,
  isStory,
  isEditorial,
  theme,
  style,
  format,
}: {
  content: MatchStoryCardContent;
  isStory: boolean;
  isEditorial: boolean;
  theme: ReturnType<typeof resolveCardTheme>;
  style: NonNullable<MatchStoryCardContent["style"]>;
  format: CardFormat;
}) {
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: theme.fontEyebrow,
        fontSizePx: 24,
        color: theme.accentColor,
        letterSpacing: "0.4em",
        bold: true,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    marginTop: isStory ? 40 : 24,
  };

  const flagWidth = isStory ? 280 : 220;
  const flagHeight = isStory ? 200 : 156;
  const nationFontSize = isStory ? 64 : 48;
  const vsFontSize = isStory ? 72 : 56;
  const dateFontSize = isStory ? 28 : 22;

  const taglineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.tagline,
      {
        fontFamily: theme.fontBody,
        fontSizePx: isStory ? 32 : 24,
        color: theme.subtleTextColor,
        italic: true,
        bold: false,
        align: "center",
      },
      format,
    ),
    lineHeight: 1.35,
    maxWidth: isStory ? 800 : 680,
    marginTop: isStory ? 40 : 28,
    marginLeft: "auto",
    marginRight: "auto",
  };

  return (
    <>
      <div style={eyebrowStyle}>{content.eyebrow || "MATCH STORY"}</div>

      {/* Flag pair + nation names */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: isStory ? 40 : 28,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isStory ? 60 : 40,
          }}
        >
          <NationBlock
            nation={content.home}
            flagWidth={flagWidth}
            flagHeight={flagHeight}
            nationFontSize={nationFontSize}
            ringColor={theme.ringColor}
            textColor={theme.headlineColor}
          />
          <div
            style={{
              fontFamily: theme.fontHeadline,
              fontSize: vsFontSize,
              fontWeight: 800,
              color: theme.accentColor,
              letterSpacing: "0.1em",
              lineHeight: 1,
            }}
          >
            vs
          </div>
          <NationBlock
            nation={content.away}
            flagWidth={flagWidth}
            flagHeight={flagHeight}
            nationFontSize={nationFontSize}
            ringColor={theme.ringColor}
            textColor={theme.headlineColor}
          />
        </div>

        {content.coverTagline && (
          <div style={taglineStyle}>
            {isEditorial ? `"${content.coverTagline}"` : content.coverTagline}
          </div>
        )}
      </div>

      {/* Date + venue strip */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          marginBottom: isStory ? 40 : 24,
        }}
      >
        {content.dateLabel && (
          <div
            style={{
              fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
              fontSize: dateFontSize,
              fontWeight: 800,
              color: theme.accentColor,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
            }}
          >
            {content.dateLabel}
          </div>
        )}
        {content.venue && (
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: isStory ? 22 : 18,
              fontWeight: 700,
              color: theme.subtleTextColor,
              letterSpacing: "0.05em",
              textAlign: "center",
            }}
          >
            {content.venue}
          </div>
        )}
      </div>
    </>
  );
}

function NationBlock({
  nation,
  flagWidth,
  flagHeight,
  nationFontSize,
  ringColor,
  textColor,
}: {
  nation: MatchResultTeam;
  flagWidth: number;
  flagHeight: number;
  nationFontSize: number;
  ringColor: string;
  textColor: string;
}) {
  const code = nation.code?.toLowerCase();
  const flagSrc =
    nation.flagUrl || (code ? `https://flagcdn.com/w640/${code}.png` : undefined);
  const isRemote = !!flagSrc && !flagSrc.startsWith("data:");
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 18,
      }}
    >
      {flagSrc && (
        <img
          src={flagSrc}
          alt={nation.name}
          crossOrigin={isRemote ? "anonymous" : undefined}
          style={{
            width: flagWidth,
            height: flagHeight,
            objectFit: "cover",
            borderRadius: 14,
            border: `3px solid ${ringColor}`,
            boxShadow: "0 14px 36px rgba(0,0,0,0.4)",
          }}
        />
      )}
      <div
        style={{
          fontFamily: "'Bebas Neue', 'Anton', sans-serif",
          fontSize: nationFontSize,
          fontWeight: 800,
          color: textColor,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {nation.name}
      </div>
    </div>
  );
}

// ─── Result page ─────────────────────────────────────────────────────────────

function ResultPage({
  content,
  isStory,
  theme,
  style,
  format,
}: {
  content: MatchStoryCardContent;
  isStory: boolean;
  theme: ReturnType<typeof resolveCardTheme>;
  style: NonNullable<MatchStoryCardContent["style"]>;
  format: CardFormat;
}) {
  const homeGoals = (content.goals ?? []).filter((g) => g.side === "home");
  const awayGoals = (content.goals ?? []).filter((g) => g.side === "away");

  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: theme.fontEyebrow,
        fontSizePx: 22,
        color: theme.accentColor,
        letterSpacing: "0.4em",
        bold: true,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    marginTop: isStory ? 32 : 20,
  };

  const scoreFontSize = isStory ? 320 : 240;
  const dashFontSize = isStory ? 200 : 150;
  const nationFontSize = isStory ? 36 : 28;
  const goalLineFontSize = isStory ? 24 : 20;
  const headlineFontSize = isStory ? 56 : 42;

  return (
    <>
      <div style={eyebrowStyle}>FULL-TIME</div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Nation names above score */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "end",
            width: "100%",
            gap: isStory ? 40 : 28,
            marginBottom: isStory ? -10 : -8,
          }}
        >
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: nationFontSize,
              fontWeight: 800,
              color: theme.subtleTextColor,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              textAlign: "right",
            }}
          >
            {content.home.name}
          </div>
          <div style={{ width: dashFontSize * 0.5 }} />
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: nationFontSize,
              fontWeight: 800,
              color: theme.subtleTextColor,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              textAlign: "left",
            }}
          >
            {content.away.name}
          </div>
        </div>

        {/* Mega score */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            width: "100%",
            gap: isStory ? 40 : 28,
            lineHeight: 0.9,
          }}
        >
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: scoreFontSize,
              fontWeight: 800,
              color: theme.headlineColor,
              textAlign: "right",
              letterSpacing: "-0.03em",
              textShadow: "0 6px 24px rgba(0,0,0,0.35)",
            }}
          >
            {content.homeScore}
          </div>
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: dashFontSize,
              fontWeight: 800,
              color: theme.accentColor,
              textAlign: "center",
              lineHeight: 1,
              opacity: 0.85,
            }}
          >
            –
          </div>
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: scoreFontSize,
              fontWeight: 800,
              color: theme.headlineColor,
              textAlign: "left",
              letterSpacing: "-0.03em",
              textShadow: "0 6px 24px rgba(0,0,0,0.35)",
            }}
          >
            {content.awayScore}
          </div>
        </div>

        {content.aggregateNote && (
          <div
            style={{
              marginTop: isStory ? 16 : 10,
              fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
              fontSize: isStory ? 22 : 18,
              fontWeight: 800,
              color: theme.accentColor,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
            }}
          >
            {content.aggregateNote}
          </div>
        )}

        {/* Goalscorer strip */}
        {(homeGoals.length > 0 || awayGoals.length > 0) && (
          <div
            style={{
              marginTop: isStory ? 48 : 32,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "start",
              width: "100%",
              gap: isStory ? 40 : 28,
            }}
          >
            <GoalColumn
              goals={homeGoals}
              align="right"
              fontSize={goalLineFontSize}
              textColor={theme.headlineColor}
              minuteColor={theme.accentColor}
            />
            <div
              style={{
                fontSize: goalLineFontSize * 1.1,
                color: theme.subtleTextColor,
                opacity: 0.55,
              }}
            >
              ⚽
            </div>
            <GoalColumn
              goals={awayGoals}
              align="left"
              fontSize={goalLineFontSize}
              textColor={theme.headlineColor}
              minuteColor={theme.accentColor}
            />
          </div>
        )}

        {content.resultHeadline && (
          <div
            style={{
              marginTop: isStory ? 56 : 36,
              fontFamily: theme.fontHeadline,
              fontSize: headlineFontSize,
              fontWeight: 800,
              color: theme.headlineColor,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              textAlign: "center",
              maxWidth: isStory ? 880 : 720,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {content.resultHeadline}
          </div>
        )}
      </div>
    </>
  );
}

function GoalColumn({
  goals,
  align,
  fontSize,
  textColor,
  minuteColor,
}: {
  goals: MatchResultGoal[];
  align: "left" | "right";
  fontSize: number;
  textColor: string;
  minuteColor: string;
}) {
  if (goals.length === 0) return <div />;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "flex-end",
        gap: 6,
      }}
    >
      {goals.map((g, i) => (
        <div
          key={i}
          style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize,
            fontWeight: 800,
            color: textColor,
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexDirection: align === "left" ? "row" : "row-reverse",
          }}
        >
          <span>{g.scorer}</span>
          <span
            style={{
              color: minuteColor,
              fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
              fontSize: fontSize * 0.85,
              fontWeight: 700,
            }}
          >
            {g.minute}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Story page ──────────────────────────────────────────────────────────────

function StoryPage({
  content,
  isStory,
  isEditorial,
  theme,
  style,
  format,
}: {
  content: MatchStoryCardContent;
  isStory: boolean;
  isEditorial: boolean;
  theme: ReturnType<typeof resolveCardTheme>;
  style: NonNullable<MatchStoryCardContent["style"]>;
  format: CardFormat;
}) {
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: theme.fontEyebrow,
        fontSizePx: 22,
        color: theme.accentColor,
        letterSpacing: "0.4em",
        bold: true,
      },
      format,
    ),
    textTransform: "uppercase",
    marginTop: isStory ? 32 : 20,
  };

  const headlineDefaultSize = isStory ? 132 : 100;
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: theme.fontHeadline,
        fontSizePx: headlineDefaultSize,
        color: theme.headlineColor,
        letterSpacing: isEditorial ? "-0.015em" : "0.005em",
        bold: true,
        italic: isEditorial,
      },
      format,
    ),
    lineHeight: 0.95,
    marginTop: isStory ? 18 : 12,
  };

  const goldStyle: CSSProperties = {
    ...resolveTextStyle(
      style.goldHighlight,
      {
        fontFamily: theme.fontHeadline,
        fontSizePx: headlineDefaultSize,
        color: theme.accentColor,
        bold: true,
        italic: isEditorial,
      },
      format,
    ),
    fontSize:
      (style.headline?.fontSizePx ??
        style.goldHighlight?.fontSizePx ??
        headlineDefaultSize) * (isStory ? 1 : 0.78),
  };

  const bodyDefaultSize = isStory ? 30 : 24;
  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: theme.fontBody,
        fontSizePx: bodyDefaultSize,
        color: theme.subtleTextColor,
        bold: false,
      },
      format,
    ),
    lineHeight: 1.45,
    marginTop: isStory ? 36 : 24,
    whiteSpace: "pre-wrap",
  };

  return (
    <>
      <div style={eyebrowStyle}>
        {(content.storyEyebrow || "THE STORY").toUpperCase()}
      </div>

      <div style={headlineStyle}>
        {content.storyHeadline}
        {content.storyGoldHighlight && (
          <>
            {" "}
            <span style={goldStyle}>{content.storyGoldHighlight}</span>
          </>
        )}
      </div>

      {/* Optional featured player chip — sits between headline and body */}
      {content.featuredPlayerName && (
        <FeaturedPlayerChip
          name={content.featuredPlayerName}
          photoUrl={content.featuredPlayerPhotoUrl}
          isStory={isStory}
          accentColor={theme.accentColor}
          textColor={theme.headlineColor}
          subtleColor={theme.subtleTextColor}
        />
      )}

      <div style={bodyStyle}>{content.storyBody}</div>
    </>
  );
}

function FeaturedPlayerChip({
  name,
  photoUrl,
  isStory,
  accentColor,
  textColor,
  subtleColor,
}: {
  name: string;
  photoUrl?: string;
  isStory: boolean;
  accentColor: string;
  textColor: string;
  subtleColor: string;
}) {
  const avatarSize = isStory ? 72 : 56;
  const isRemote = !!photoUrl && !photoUrl.startsWith("data:");
  return (
    <div
      style={{
        marginTop: isStory ? 28 : 18,
        display: "inline-flex",
        alignItems: "center",
        gap: 14,
        padding: `${isStory ? 10 : 8}px ${isStory ? 22 : 16}px ${isStory ? 10 : 8}px ${isStory ? 10 : 8}px`,
        borderRadius: 9999,
        background: "rgba(255,255,255,0.08)",
        border: `1px solid ${accentColor}33`,
        alignSelf: "flex-start",
      }}
    >
      <div
        style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: 9999,
          overflow: "hidden",
          background: "rgba(0,0,0,0.4)",
          border: `2px solid ${accentColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name}
            crossOrigin={isRemote ? "anonymous" : undefined}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: avatarSize * 0.5,
              fontWeight: 800,
              color: textColor,
            }}
          >
            {name
              .split(/\s+/)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase() ?? "")
              .join("")}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            fontSize: isStory ? 12 : 10,
            fontWeight: 800,
            color: subtleColor,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
          }}
        >
          Story by
        </div>
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: isStory ? 22 : 18,
            fontWeight: 800,
            color: textColor,
            letterSpacing: "-0.01em",
          }}
        >
          {name}
        </div>
      </div>
    </div>
  );
}
